/**
 * PartyTaxInfo
 *
 * GST registration type, and dynamic country-specific business identifier fields
 * (GSTIN, PAN, CIN, TAN for India; other identifiers for other countries).
 */

import {
  gstRegistrationTypeToString,
  stringToGSTRegistrationType,
} from '../../../utils/billing-helpers';
import {
  FormField,
  Input,
  Select,
} from '../../ui';

import type { PartyFormData } from './party-dialog-helpers';
import type { CountryIdentifier } from './usePartyDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PartyTaxInfoProps {
  formData: PartyFormData;
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>;
  errors: Record<string, string>;
  selectedEntity: { id: string } | null;
  countryIdentifiers: CountryIdentifier[];
  billingCountryName?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GST_REGISTRATION_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'composition', label: 'Composition' },
  { value: 'unregistered', label: 'Unregistered' },
  { value: 'consumer', label: 'Consumer' },
  { value: 'overseas', label: 'Overseas' },
  { value: 'sez', label: 'SEZ' },
]

interface IdentifierFieldProps {
  identifier: PartyTaxInfoProps['countryIdentifiers'][number]
  formData: PartyTaxInfoProps['formData']
  setFormData: PartyTaxInfoProps['setFormData']
  errors: PartyTaxInfoProps['errors']
  selectedEntity: PartyTaxInfoProps['selectedEntity']
}

function IdentifierField({ identifier, formData, setFormData, errors, selectedEntity }: IdentifierFieldProps): JSX.Element {
  return (
    <FormField
      key={identifier.key}
      compact
      label={identifier.label}
      required={identifier.isRequired}
      {...(errors[identifier.key] ? { error: errors[identifier.key] } : {})}
    >
      <Input
        disabled={!!selectedEntity}
        maxLength={identifier.maxLength}
        placeholder={identifier.placeholder}
        // eslint-disable-next-line @typescript-eslint/no-base-to-string -- TODO(refactor): review and fix properly
        value={String((formData as unknown as Record<string, unknown>)[identifier.key] ?? '')}
        onChange={e => setFormData(prev => ({
          ...prev,
          [identifier.key]: e.target.value.toUpperCase(),
        }))}
      />
    </FormField>
  )
}

function GstRegistrationField({ formData, setFormData }: Pick<PartyTaxInfoProps, 'formData' | 'setFormData'>): JSX.Element {
  return (
    <FormField compact label="GST Registration Type">
      <Select
        options={GST_REGISTRATION_OPTIONS}
        value={gstRegistrationTypeToString(formData.gstRegistrationType)}
        onChange={value => setFormData(prev => ({
          ...prev,
          gstRegistrationType: stringToGSTRegistrationType(value),
        }))}
      />
    </FormField>
  )
}

export function PartyTaxInfo({
  formData,
  setFormData,
  errors,
  selectedEntity,
  countryIdentifiers,
  billingCountryName,
}: PartyTaxInfoProps): JSX.Element | null {
  if (countryIdentifiers.length === 0) { return null; }
  return (
    <div>
      <h3 className="text-base font-semibold text-foreground el-section-heading el-mb-sm border-b border-border">
        Business Identifiers
        <span className="ml-2 text-sm font-normal text-foreground-secondary">
          ({billingCountryName ?? 'India'})
        </span>
      </h3>
      <div className="grid grid-cols-2 el-gap-field el-mt-sm">
        {countryIdentifiers.map(identifier => (
          <IdentifierField
            key={identifier.key}
            errors={errors}
            formData={formData}
            identifier={identifier}
            selectedEntity={selectedEntity}
            setFormData={setFormData}
          />
        ))}
        {formData.billingAddress.countryCode === 'IN' && (
          <GstRegistrationField formData={formData} setFormData={setFormData} />
        )}
      </div>
    </div>
  );
}
